import { useAppStateStore } from '@src/providers/AppStateProvider';
import {
  CUSTOMER_QUOTE,
  QUOTE,
  QuoteStatusEnum,
  formattedDateForSubscription,
  useBehaviorSubject,
  useEndpointsContext,
  useServiceInstantiator,
  useStaticSubscription,
} from '@talos/kyoko';
import { useEffect, useMemo } from 'react';
import { useFeatureFlag, useUser } from '../../../hooks';
import { OrgConfigurationKey, useOrders, useOrgConfiguration } from '../../../providers';
import {
  useLazyGetCareOrdersQuery,
  useLazyGetWorkingOrdersQuery,
} from '../../../providers/AppStateProvider/streamingDataSlice';
import { CardsService } from './CardsService';

export const useCardsService = (sessionStart = new Date()) => {
  const store = useAppStateStore();
  const user = useUser();
  const { orgApiEndpoint } = useEndpointsContext();
  const { getConfig } = useOrgConfiguration();
  const { enableExecutingOtherUserNegotiatedRFQ, enableTakerMarketplaceETFRFQFlow } = useFeatureFlag();

  const customerSummaryDelayMs = useMemo(() => getConfig(OrgConfigurationKey.CustomerSummaryDelayMs, 0), [getConfig]);

  const { recentOrders } = useOrders();

  const { data: filledQuotesSub } = useStaticSubscription({
    name: QUOTE,
    tag: 'CardsService',
    HideAPICalls: true,
    // PendingFix is for Quotes tied to a CareOrder - they go to PendingFix and are left like that for the rest of the day.
    Statuses: [QuoteStatusEnum.PendingFix, QuoteStatusEnum.Filled],
    ...(enableExecutingOtherUserNegotiatedRFQ ? {} : { User: user?.Name }),
    // This seems implicit in regular RFQ - we only get subsequent Quotes
    StartDate: formattedDateForSubscription(sessionStart),
  });

  const { data: customerFilledQuoteSub } = useStaticSubscription({
    name: CUSTOMER_QUOTE,
    tag: 'CardsService',
    HideAPICalls: true,
    Statuses: [QuoteStatusEnum.Filled],
    // We need to explicitly set this or else we retrieve all Filled quotes even historical
    StartDate: formattedDateForSubscription(sessionStart),
  });

  const [triggerCareOrdersRequest, { data: careOrders }] = useLazyGetCareOrdersQuery();
  const { observable: careOrdersObs } = useBehaviorSubject(() => careOrders ?? new Map(), [careOrders]);
  const [triggerWorkingOrdersRequest, { data: workingOrders }] = useLazyGetWorkingOrdersQuery();
  const { observable: workingOrdersObs } = useBehaviorSubject(() => workingOrders ?? new Map(), [workingOrders]);
  useEffect(() => {
    if (enableTakerMarketplaceETFRFQFlow) {
      // Only kick off the request if the flag is enabled
      triggerCareOrdersRequest({});
      triggerWorkingOrdersRequest({});
    }
  }, [enableTakerMarketplaceETFRFQFlow, triggerCareOrdersRequest, triggerWorkingOrdersRequest]);

  const cardsService = useServiceInstantiator(() => {
    return new CardsService(
      store,
      recentOrders,
      filledQuotesSub,
      customerFilledQuoteSub,
      careOrdersObs,
      workingOrdersObs,
      orgApiEndpoint!,
      user?.Name,
      customerSummaryDelayMs
    );
  }, [
    store,
    recentOrders,
    filledQuotesSub,
    customerFilledQuoteSub,
    orgApiEndpoint,
    user?.Name,
    customerSummaryDelayMs,
    careOrdersObs,
    workingOrdersObs,
  ]);
  return cardsService;
};
